import React from 'react'
import { IExtendedColumn } from '../../interfaces/IColumn'
import { enumValuesToTranslate } from '../../helpers/enumHelper'
import { Demand, DemandLevel, QuestionDTO, QuestionStatus, SubscriptionLevel } from '../../api/schemas/schema'
import { NavLink } from 'react-router-dom'
import StatusPill from '../../components/common/status/StatusPill'
import { statusBadgeColor } from '../../helpers/demandHelper'
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { RTFOutput } from '../../components/common/outputs/RTFOutput'
import { Text } from '../../components/common/texts/Text'
import { HighlightedValue } from '../../components/common/outputs/HighlightedValue'
type ReorderQuestionRowProps = {
   columns: IExtendedColumn[]
   rowClass: string
   demand: Demand
   question: QuestionDTO
   /** Renders the row as a standalone search hit: no level-based indentation. */
   flat?: boolean
   /** Replaces the truncated RTF body cell. */
   textContent?: JSX.Element
   /** Normalized. Marks matches in the text columns; the status pill is left alone. */
   highlightQuery?: string
}

const useStyles = makeStyles({
   customTableRow: {
      backgroundColor: tokens.colorPaletteMarigoldBackground1,
      '&:hover': {
         backgroundColor: tokens.colorPaletteMarigoldBackground2,
      },
   },
   questionLabel: {
      display: 'flex',
      justifyContent: 'center',
   },
   label: {
      fontWeight: tokens.fontWeightBold,
   },
   spacingHorizontalMNudge: {
      marginRight: tokens.spacingHorizontalMNudge,
   },
   spacingHorizontalXXXL: {
      marginRight: tokens.spacingHorizontalXXXL,
   },
})

export const CustomQuestionRow = ({ rowClass, columns, demand, question, flat, textContent, highlightQuery }: ReorderQuestionRowProps) => {
   const classes = useStyles()

   const calculateQuestionTextWidth = () => {
      let width = 0
      for (let index = 4; index < 8; index++) {
         const widthValue = columns[index].width
         width += parseInt(widthValue.replace('px', ''), 10)
      }
      return `${width}%`
   }

   return (
      <div className={mergeClasses(rowClass, classes.customTableRow)}>
         <span style={{ width: columns[0].width, minWidth: columns[0].minWidth }}></span>
         <span style={{ width: columns[1].width, minWidth: columns[1].minWidth }}>
            <div
               className={`${classes.questionLabel} ${
                  flat
                     ? ''
                     : demand.level === DemandLevel.Two
                     ? classes.spacingHorizontalMNudge
                     : demand.level === DemandLevel.One
                     ? classes.spacingHorizontalXXXL
                     : null
               }`}
            >
               <Text as="strong" className={classes.label}>
                  {t('Question')}
               </Text>
            </div>
         </span>
         <span style={{ width: columns[2].width, minWidth: columns[2].minWidth }}>
            <HighlightedValue value={`${demand.publicId}.${question.publicId}`} query={highlightQuery} />
         </span>
         <span style={{ width: columns[3].width, minWidth: columns[3].minWidth }}>
            <HighlightedValue value={question.designation} query={highlightQuery} />
         </span>
         <span style={{ width: calculateQuestionTextWidth() }}>
            <NavLink style={{ textDecoration: 'none', color: 'black' }} to={`/demands/${demand.id}?question=${question.order}`}>
               {textContent ?? <RTFOutput text={question.text?.length > 300 ? question.text.substring(0, 300) + '...' : question.text ?? ''} fontSize={300} noMargin />}
            </NavLink>
         </span>

         <span style={{ width: columns[8].width, minWidth: columns[8].minWidth }}>
            <HighlightedValue
               value={question.subscriptions?.map((sConn) => enumValuesToTranslate(SubscriptionLevel, sConn.subscriptionLevel)).join(' ') ?? ''}
               query={highlightQuery}
            />
         </span>

         <span style={{ width: columns[9].width, minWidth: columns[9].minWidth }}>
            <StatusPill color={statusBadgeColor(question.status)}>{enumValuesToTranslate(QuestionStatus, question.status)}</StatusPill>
         </span>
         <span style={{ width: columns[10].width, minWidth: columns[10].minWidth }} />
      </div>
   )
}
